import { useState } from 'react';
import { Link } from 'react-router';
import { AlertCircle, CheckCircle2, ExternalLink, Plus, Trash2 } from 'lucide-react';
import { Button, Input } from '@helios/ui';
import type { CompanySetupPerson, CompanySetupProvider, CompanySetupView } from '@helios/types';
import { apiClient } from '@/services/apiClient';
import { getSessionOriginKey, isCurrentSessionOrigin } from '@/services/sessionOrigin';
import { companySetupClient, type SetupDraft } from '@/services/companySetupClient';
import { CanonicalProfileLink } from '@/features/unified-crm/CanonicalProfileLink';
import { useAuth } from '@/features/auth/authContext';
import { SetupCheck, SetupField } from './SetupControls';

interface SectionProps {
  view: CompanySetupView;
  draft: SetupDraft;
  update: (patch: Partial<SetupDraft>) => void;
}
export function SetupCompanyForm({ view, draft, update }: SectionProps) {
  const set = (patch: Partial<SetupDraft['profile']>) =>
    update({ profile: { ...draft.profile, ...patch } });
  return (
    <section>
      <h2>Company details</h2>
      <div className="company-setup-form-grid">
        <SetupField label="Legal company name">
          <Input
            value={draft.profile.legalName}
            maxLength={160}
            onChange={(e) => set({ legalName: e.target.value })}
          />
        </SetupField>
        <SetupField label="Workspace name">
          <Input
            value={draft.profile.workspaceName}
            maxLength={160}
            onChange={(e) => set({ workspaceName: e.target.value })}
          />
        </SetupField>
        <SetupField
          label="Workspace address"
          hint="Reserved for this company by the platform administrator."
        >
          <Input value={view.slug} readOnly />
        </SetupField>
        <SetupField label="Timezone">
          <select
            value={draft.profile.timezone}
            onChange={(e) => set({ timezone: e.target.value })}
          >
            {[
              ...new Set([
                draft.profile.timezone,
                'Etc/UTC',
                'America/New_York',
                'America/Chicago',
                'America/Denver',
                'America/Los_Angeles',
                'Europe/London',
              ]),
            ].map((zone) => (
              <option key={zone}>{zone}</option>
            ))}
          </select>
        </SetupField>
        <SetupField label="Currency" hint="Current financial workflows use US dollars.">
          <Input value="USD" readOnly />
        </SetupField>
        <SetupField label="Fiscal year starts">
          <select
            value={draft.profile.fiscalYearMonth}
            onChange={(e) => set({ fiscalYearMonth: Number(e.target.value) })}
          >
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i} value={i + 1}>
                {new Intl.DateTimeFormat('en', { month: 'long', timeZone: 'UTC' }).format(
                  new Date(Date.UTC(2020, i, 1)),
                )}
              </option>
            ))}
          </select>
        </SetupField>
      </div>
      <div className="company-setup-divider">
        <h2>Your setup priorities</h2>
        <div className="company-setup-priorities">
          {['Projects', 'Finance', 'People', 'Documents'].map((name) => (
            <SetupCheck
              key={name}
              checked={draft.profile.priorities.includes(name)}
              onChange={(value) =>
                set({
                  priorities: value
                    ? [...draft.profile.priorities, name]
                    : draft.profile.priorities.filter((p) => p !== name),
                })
              }
            >
              {name}
            </SetupCheck>
          ))}
        </div>
        <p className="company-setup-muted">Nothing is imported or sent until you review it.</p>
      </div>
    </section>
  );
}

export function SetupPeople({ view, draft, update }: SectionProps) {
  const change = (index: number, patch: Partial<CompanySetupPerson>) =>
    update({
      roster: draft.roster.map((p, i) => (i === index ? { ...p, ...patch } : p)),
      accessApproved: false,
    });
  return (
    <section>
      <h2>People & access</h2>
      <p>
        Employee records and CRM contacts do not automatically receive app access. Titles never
        grant permissions.
      </p>
      <div className="company-setup-owner">
        <CheckCircle2 size={18} />
        <div>
          <strong>{view.ownerName}</strong>
          <span>{view.ownerEmail} · Verified company owner · Company administrator</span>
        </div>
      </div>
      <p>
        <strong>
          {1 + draft.roster.filter((p) => p.loginRequested).length} / {view.licensedSeats}
        </strong>{' '}
        reviewed app-user seats, including the owner. No subscription or charge is created.
      </p>
      <div className="company-setup-people">
        {draft.roster.map((person, i) => (
          <div key={i} className="company-setup-person">
            <div className="company-setup-form-grid">
              <SetupField label={`Person ${i + 1} name`}>
                <Input
                  value={person.name}
                  maxLength={120}
                  onChange={(e) => change(i, { name: e.target.value })}
                />
              </SetupField>
              <SetupField label={`Person ${i + 1} email`}>
                <Input
                  type="email"
                  value={person.email}
                  onChange={(e) => change(i, { email: e.target.value })}
                />
              </SetupField>
              <SetupField label={`Person ${i + 1} title`}>
                <Input
                  value={person.title}
                  onChange={(e) => change(i, { title: e.target.value })}
                />
              </SetupField>
              <SetupField label={`Person ${i + 1} record type`}>
                <select
                  value={person.kind}
                  onChange={(e) =>
                    change(i, {
                      kind: e.target.value as CompanySetupPerson['kind'],
                      ...(e.target.value === 'CONTACT' ? { loginRequested: false } : {}),
                    })
                  }
                >
                  <option value="APP_USER">App user</option>
                  <option value="EMPLOYEE">Employee</option>
                  <option value="CONTACT">CRM contact plan</option>
                </select>
              </SetupField>
              <SetupField label={`Person ${i + 1} role`}>
                <select
                  value={person.role}
                  disabled={!person.loginRequested}
                  onChange={(e) =>
                    change(i, { role: e.target.value as CompanySetupPerson['role'] })
                  }
                >
                  {view.roleDefinitions.map((role) => (
                    <option key={role.key} value={role.key}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </SetupField>
              <div className="company-setup-person-actions">
                <SetupCheck
                  checked={person.loginRequested}
                  disabled={person.kind === 'CONTACT'}
                  onChange={(value) => change(i, { loginRequested: value })}
                >
                  App access requested
                </SetupCheck>
                <Button
                  variant="ghost"
                  aria-label={`Remove person ${i + 1}`}
                  onClick={() =>
                    update({
                      roster: draft.roster.filter((_, index) => index !== i),
                      accessApproved: false,
                    })
                  }
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
            {person.kind === 'CONTACT' && (
              <small>
                Queued as a source-backed contact after owner-approved launch. App access and
                business roles are reviewed separately.
              </small>
            )}
          </div>
        ))}
      </div>
      <Button
        variant="outline"
        disabled={draft.roster.length >= 499}
        onClick={() =>
          update({
            roster: [
              ...draft.roster,
              {
                name: '',
                email: '',
                title: '',
                kind: 'APP_USER',
                loginRequested: true,
                role: 'viewer',
              },
            ],
            accessApproved: false,
          })
        }
      >
        <Plus size={16} />
        Add person
      </Button>
      <details className="company-setup-divider">
        <summary>Review the exact role permissions</summary>
        {view.roleDefinitions.map((role) => (
          <details key={role.key}>
            <summary>
              {role.label} · {role.permissions.length} permissions
            </summary>
            <ul className="company-setup-permissions">
              {role.permissions.map((key) => (
                <li key={key}>{key}</li>
              ))}
            </ul>
          </details>
        ))}
      </details>
      <div className="company-setup-divider">
        <SetupCheck
          checked={draft.accessApproved}
          disabled={!view.canApproveReviews}
          onChange={(value) => update({ accessApproved: value })}
        >
          I reviewed every named app user, record type, seat and role. No customer receives platform
          administration.
        </SetupCheck>
        <small>
          {!view.canApproveReviews && 'Only the verified company owner can approve this review. '}
          Save the roster first, then approve its saved version. Changes reset approval. Credentials
          are provisioned separately after launch.
        </small>
      </div>
    </section>
  );
}

const PROVIDERS: Array<{
  key: CompanySetupProvider['key'];
  name: string;
  hint: string;
  connect: boolean;
}> = [
  {
    key: 'microsoft365',
    name: 'Microsoft 365',
    hint: 'Connect your personal workspace. App sign-in and company-wide admin consent are separate.',
    connect: true,
  },
  {
    key: 'quickbooks_online',
    name: 'QuickBooks Online',
    hint: 'Authorize the exact accounting company. Desktop migration history is a separate decision.',
    connect: true,
  },
  {
    key: 'quickbooks_desktop',
    name: 'QuickBooks Desktop',
    hint: 'Delegate bridge setup to your accounting administrator. No automatic Desktop/Online merge.',
    connect: false,
  },
  {
    key: 'procore',
    name: 'Procore',
    hint: 'Authorize the correct company. Setup never starts the existing bidirectional sync.',
    connect: true,
  },
  {
    key: 'plaid',
    name: 'Banking / Plaid',
    hint: 'Bank authorization is handled in the Banking module after launch.',
    connect: false,
  },
  {
    key: 'dropbox',
    name: 'Dropbox',
    hint: 'Connect and choose authorized files in Integrations after launch.',
    connect: false,
  },
];
type Run = (work: () => Promise<void>) => Promise<void>;
export function SetupConnections({
  view,
  draft,
  update,
  dirty,
  canConnect,
  busy,
  onRun,
  onRefresh,
}: SectionProps & {
  dirty: boolean;
  canConnect: boolean;
  busy: boolean;
  onRun: Run;
  onRefresh: () => Promise<void>;
}) {
  const set = (key: string, patch: Partial<CompanySetupProvider>) =>
    update({
      providers: draft.providers.map((p) => (p.key === key ? { ...p, ...patch } : p)),
      sourceApproved: false,
    });
  const connect = async (key: string) => {
    const origin = getSessionOriginKey();
    const options = { sessionOrigin: origin, allowRefresh: false };
    const result =
      key === 'microsoft365'
        ? await apiClient.get<{ authorizationUrl: string | null; detail?: string }>(
            '/v1/m365-workspace/oauth-url',
            options,
          )
        : await apiClient.post<{ authorizationUrl: string | null; reason?: string }>(
            key === 'procore'
              ? '/v1/integrations/procore/oauth/start'
              : '/v1/accounting/connections/oauth/start',
            {},
            options,
          );
    if (!isCurrentSessionOrigin(origin))
      throw new Error('Your sign-in changed. Restart Connect from your current company.');
    if (!result.ok || !result.data?.authorizationUrl)
      throw new Error(
        result.error ??
          'This provider is not configured. Delegate the setup task or explicitly defer it.',
      );
    const url = new URL(result.data.authorizationUrl);
    if (
      !['https:', ...(window.location.hostname === '127.0.0.1' ? ['http:'] : [])].includes(
        url.protocol,
      )
    )
      throw new Error('The provider returned an invalid authorization address');
    window.location.assign(url.toString());
  };
  return (
    <section>
      <h2>Connections</h2>
      <p>
        Authorize providers on their own secure screens. Passwords, tokens, and company-admin
        credentials never belong in chat.
      </p>
      {!canConnect && (
        <p className="company-setup-warning">
          The verified owner connects providers from the new company’s own session.
        </p>
      )}
      {dirty && (
        <p className="company-setup-warning">
          Save your plan before opening a provider. Your progress will be waiting when you return.
        </p>
      )}
      <div className="company-setup-provider-list">
        {PROVIDERS.map((provider) => {
          const plan = draft.providers.find((p) => p.key === provider.key);
          const state = view.providerStates.find((s) => s.key === provider.key);
          return (
            <article key={provider.key}>
              <SetupCheck
                checked={Boolean(plan)}
                onChange={(value) =>
                  update({
                    providers: value
                      ? [
                          ...draft.providers,
                          {
                            key: provider.key,
                            required: false,
                            authority: 'UNDECIDED',
                            delegateEmail: '',
                            note: '',
                          },
                        ]
                      : draft.providers.filter((p) => p.key !== provider.key),
                    sourceApproved: false,
                  })
                }
              >
                <strong>{provider.name}</strong>
              </SetupCheck>
              <p>{provider.hint}</p>
              {plan && (
                <>
                  <div className="company-setup-provider-state">
                    <span>{state?.state.replaceAll('_', ' ') ?? 'Save plan to check status'}</span>
                    {state?.company && <strong>{state.company}</strong>}
                  </div>
                  <div className="company-setup-form-grid">
                    <SetupField label={`${provider.name} administrator email`}>
                      <Input
                        type="email"
                        value={plan.delegateEmail}
                        onChange={(e) => set(provider.key, { delegateEmail: e.target.value })}
                      />
                    </SetupField>
                    {provider.key.startsWith('quickbooks') && (
                      <SetupField label={`${provider.name} source of truth`}>
                        <select
                          value={plan.authority}
                          onChange={(e) =>
                            set(provider.key, {
                              authority: e.target.value as CompanySetupProvider['authority'],
                            })
                          }
                        >
                          <option value="UNDECIDED">Choose books authority</option>
                          <option value="PRIMARY">Authoritative books</option>
                          <option value="HISTORICAL">Historical reference only</option>
                        </select>
                      </SetupField>
                    )}
                    <SetupField label={`${provider.name} setup note`}>
                      <Input
                        value={plan.note}
                        maxLength={500}
                        onChange={(e) => set(provider.key, { note: e.target.value })}
                      />
                    </SetupField>
                  </div>
                  <div className="company-setup-provider-actions">
                    <SetupCheck
                      checked={plan.required}
                      onChange={(value) => set(provider.key, { required: value })}
                    >
                      Required before launch
                    </SetupCheck>
                    {provider.connect ? (
                      <Button
                        variant="outline"
                        disabled={busy || dirty || !canConnect || state?.state === 'NOT_CONFIGURED'}
                        onClick={() => void onRun(() => connect(provider.key))}
                      >
                        {state?.state === 'CONNECTED' ? 'Reconnect' : 'Connect'} {provider.name}
                        <ExternalLink size={14} />
                      </Button>
                    ) : (
                      <small>Complete after launch or track a delegated task below.</small>
                    )}
                  </div>
                  {state && (
                    <small>
                      {state.detail}
                      {state.lastSyncAt
                        ? ` Last successful sync: ${new Date(state.lastSyncAt).toLocaleString()}.`
                        : ' No successful business sync recorded.'}
                    </small>
                  )}
                </>
              )}
            </article>
          );
        })}
      </div>
      <Button variant="outline" disabled={busy || dirty} onClick={() => void onRefresh()}>
        Refresh connection state
      </Button>
      <div className="company-setup-divider">
        <h2>Delegated setup tasks</h2>
        <p>
          Assign a responsibility and track the result. Saving a task does not send an email or
          grant account access.
        </p>
        {draft.tasks.map((task, index) => (
          <div className="company-setup-task" key={task.id}>
            <Input
              aria-label={`Task ${index + 1} title`}
              placeholder="Task"
              value={task.title}
              onChange={(e) =>
                update({
                  tasks: draft.tasks.map((t) =>
                    t.id === task.id ? { ...t, title: e.target.value } : t,
                  ),
                })
              }
            />
            <Input
              aria-label={`Task ${index + 1} assignee`}
              type="email"
              placeholder="Administrator email"
              value={task.assignedEmail}
              onChange={(e) =>
                update({
                  tasks: draft.tasks.map((t) =>
                    t.id === task.id ? { ...t, assignedEmail: e.target.value } : t,
                  ),
                })
              }
            />
            <select
              aria-label={`Task ${index + 1} status`}
              value={task.status}
              onChange={(e) =>
                update({
                  tasks: draft.tasks.map((t) =>
                    t.id === task.id ? { ...t, status: e.target.value as typeof task.status } : t,
                  ),
                })
              }
            >
              {['TODO', 'BLOCKED', 'DONE', 'DEFERRED'].map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
            <Input
              aria-label={`Task ${index + 1} evidence`}
              placeholder="Outcome or dependency"
              value={task.note}
              onChange={(e) =>
                update({
                  tasks: draft.tasks.map((t) =>
                    t.id === task.id ? { ...t, note: e.target.value } : t,
                  ),
                })
              }
            />
            <Button
              variant="ghost"
              aria-label={`Remove task ${index + 1}`}
              onClick={() => update({ tasks: draft.tasks.filter((t) => t.id !== task.id) })}
            >
              <Trash2 size={16} />
            </Button>
          </div>
        ))}
        <Button
          variant="outline"
          onClick={() =>
            update({
              tasks: [
                ...draft.tasks,
                { id: crypto.randomUUID(), title: '', assignedEmail: '', status: 'TODO', note: '' },
              ],
            })
          }
        >
          <Plus size={16} />
          Add setup task
        </Button>
      </div>
    </section>
  );
}

export function SetupImportReview({ view, draft, update }: SectionProps) {
  const [preview, setPreview] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const readPreview = async (key: string) => {
    setBusy(true);
    setError(null);
    setPreview(null);
    try {
      setPreview(await companySetupClient.preview(view.id, view.version, key));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Provider preview failed. No import was started.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <section>
      <h2>Import review</h2>
      <p>
        Confirm the exact provider company and the record types you intend to use. Existing
        workspaces and their records stay separate.
      </p>
      <SetupField label="Launch data plan">
        <select
          value={draft.importMode}
          onChange={(e) =>
            update({
              importMode: e.target.value as SetupDraft['importMode'],
              sourceApproved: false,
            })
          }
        >
          <option value="START_EMPTY">Start with an empty company</option>
          <option value="IMPORT_LATER">Review and import from modules after launch</option>
        </select>
      </SetupField>
      <p className="company-setup-warning">
        Company setup does not import transactions or run bidirectional sync. After launch, use the
        existing module import and mapping controls. New dashboards show unavailable data until
        records exist.
      </p>
      <table className="company-setup-review-table">
        <thead>
          <tr>
            <th>Provider</th>
            <th>Authority</th>
            <th>Saved company / account</th>
          </tr>
        </thead>
        <tbody>
          {draft.providers.length ? (
            draft.providers.map((p) => (
              <tr key={p.key}>
                <td>{PROVIDERS.find((x) => x.key === p.key)?.name}</td>
                <td>{p.authority}</td>
                <td>
                  {view.providerStates.find((s) => s.key === p.key)?.company ?? 'Not connected'}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={3}>No providers selected. You can connect them later.</td>
            </tr>
          )}
        </tbody>
      </table>
      <h3>Read-only provider checks</h3>
      <p>
        These checks use the selected company’s saved authorization, may refresh its token, and
        import no business records. Save your connection decisions first.
      </p>
      <div className="company-setup-inline-actions">
        {draft.providers
          .filter((p) => p.key === 'quickbooks_online' || p.key === 'microsoft365')
          .map((p) => (
            <Button
              key={p.key}
              disabled={busy || JSON.stringify(draft.providers) !== JSON.stringify(view.providers)}
              variant="outline"
              onClick={() => void readPreview(p.key)}
            >
              Preview{' '}
              {p.key === 'quickbooks_online' ? 'QuickBooks references' : 'Microsoft account'}
            </Button>
          ))}
      </div>
      {error && (
        <p role="alert" className="company-setup-error">
          {error}
        </p>
      )}
      {preview && (
        <div className="company-setup-preview">
          <h3>Actual provider response</h3>
          <pre>{JSON.stringify(preview, null, 2)}</pre>
        </div>
      )}
      <div className="company-setup-divider">
        <SetupCheck
          checked={draft.sourceApproved}
          disabled={!view.canApproveReviews}
          onChange={(value) => update({ sourceApproved: value })}
        >
          I reviewed provider identities, accounting authority, duplicate risks, and the
          empty/deferred import plan. No automatic merge or external write is approved.
        </SetupCheck>
        <small>
          {!view.canApproveReviews && 'Only the verified company owner can approve this review. '}
          Save source changes first, then approve the saved plan. Changing providers resets this
          review.
        </small>
      </div>
    </section>
  );
}

export function SetupLaunch({
  view,
  busy,
  canActivate,
  onActivate,
  onRun,
}: {
  view: CompanySetupView;
  busy: boolean;
  canActivate: boolean;
  onActivate: () => void;
  onRun: Run;
}) {
  const auth = useAuth();
  const sameWorkspace = auth.tenant?.id === view.tenantId;
  const [credential, setCredential] = useState<{
    email: string;
    password: string | null;
    state: string;
  } | null>(null);
  return (
    <section>
      <h2>{view.status === 'ACTIVE' ? 'Continue onboarding' : 'Review and launch'}</h2>
      <p>
        {view.profile.workspaceName} · {view.slug} · {view.profile.timezone} ·{' '}
        {view.profile.currency}
      </p>
      <div className="company-setup-summary">
        <p>
          <strong>Verified owner</strong>
          <span>
            {view.ownerName} · {view.ownerEmail}
          </span>
        </p>
        <p>
          <strong>App users requested</strong>
          <span>
            {1 + view.roster.filter((p) => p.loginRequested).length} of {view.licensedSeats}{' '}
            reviewed seats
          </span>
        </p>
        <p>
          <strong>Data plan</strong>
          <span>
            {view.importMode === 'START_EMPTY'
              ? 'Start empty'
              : 'Import later through reviewed module workflows'}
          </span>
        </p>
      </div>
      {view.status !== 'ACTIVE' && (
        <>
          <ul className="company-setup-readiness">
            {view.blockers.length ? (
              view.blockers.map((blocker) => (
                <li key={blocker}>
                  <AlertCircle size={17} />
                  {blocker}
                </li>
              ))
            ) : (
              <li className="company-setup-notice">
                <CheckCircle2 size={17} />
                Required setup reviews are complete.
              </li>
            )}
          </ul>
          <p>
            Launch creates the reviewed directory records and company roles. It sends no
            invitations, imports no records, and creates no charge. Your setup session ends so you
            can sign in with the new company permissions.
          </p>
          <Button disabled={busy || !canActivate} onClick={onActivate}>
            Activate company and sign in
          </Button>
        </>
      )}
      {view.status === 'ACTIVE' && (
        <>
          <div className="company-setup-inline-actions">
            <Button asChild>
              <Link to="/">Enter platform</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/settings/users">Manage ongoing user access</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/integrations">Manage integrations</Link>
            </Button>
          </div>
          {view.roster.some((person) => person.kind === 'CONTACT') && (
            <div className="company-setup-divider">
              <h3>Submitted contacts</h3>
              <p>Profiles become available after authorized ingestion. No app access is granted.</p>
              {view.roster.map(
                (person, index) =>
                  person.kind === 'CONTACT' && (
                    <div key={index} className="company-setup-provision-row">
                      <div>
                        <strong>{person.name}</strong>
                        <small>{person.email}</small>
                      </div>
                      {!sameWorkspace ? (
                        <small>Open this company's workspace to view its contact profile.</small>
                      ) : view.contactSubmissionEventId ? (
                        <CanonicalProfileLink
                          objectType="CompanySetupContact"
                          objectId={`${view.id}:${view.contactSubmissionEventId}:${index}`}
                        >
                          Contact profile
                        </CanonicalProfileLink>
                      ) : (
                        <small>No contact ingestion receipt is available.</small>
                      )}
                    </div>
                  ),
              )}
            </div>
          )}
          <h3>Approved app users</h3>
          <p>
            Issue credentials separately. Each enabled account consumes a reviewed seat. Temporary
            credentials expire after 24 hours and require a password change.
          </p>
          {view.roster
            .filter((p) => p.loginRequested && p.kind !== 'CONTACT')
            .map((person) => (
              <div key={person.email} className="company-setup-provision-row">
                <div>
                  <strong>{person.name}</strong>
                  <small>
                    {person.email} · {person.role}
                  </small>
                </div>
                <Button
                  variant="outline"
                  disabled={busy}
                  onClick={() =>
                    void onRun(async () => {
                      const result = await companySetupClient.provision(
                        view.id,
                        person.email,
                        'DISPLAY',
                      );
                      setCredential({
                        email: person.email,
                        password: result.temporaryPassword,
                        state: result.deliveryState,
                      });
                    })
                  }
                >
                  Issue secure handoff
                </Button>
                <Button
                  variant="outline"
                  disabled={busy || view.mailTransport === 'log'}
                  onClick={() =>
                    void onRun(async () => {
                      const result = await companySetupClient.provision(
                        view.id,
                        person.email,
                        'EMAIL',
                      );
                      setCredential({
                        email: person.email,
                        password: result.temporaryPassword,
                        state: result.deliveryState,
                      });
                    })
                  }
                >
                  Send credential email
                </Button>
              </div>
            ))}
          {credential && (
            <div className="company-setup-handoff" role="status">
              <strong>
                {credential.email} · {credential.state}
              </strong>
              {credential.password && (
                <>
                  <p>
                    Give this credential only to the named person through an approved secure
                    channel. It is shown once.
                  </p>
                  <code>{credential.password}</code>
                </>
              )}
              <Button variant="outline" onClick={() => setCredential(null)}>
                Close credential display
              </Button>
            </div>
          )}
        </>
      )}
      <div className="company-setup-divider">
        <h3>Dependencies and operating limits</h3>
        <ul>
          {view.warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
        <p>
          External licenses, provider rate limits, storage, and commercial terms must be reviewed
          with the platform owner. Setup has no automatic purchases or paid overages. AI has a
          maximum of 20 setup requests; normal provider/platform costs still apply.
        </p>
      </div>
    </section>
  );
}
